"use client";

// ============================================================
// CLUBHUB – Pinnwand: der Bestand im Browser
// ============================================================
// Wie bei den Gruppen: das Layout von /pinnwand reicht den Bestand
// einmal herein, beide Register (Aushaenge, Aufgaben) lesen und
// aendern ihn hier.
//
//   quelle „muster“     (ohne Sitzung) Aenderungen bleiben im Browser,
//                       auch beim Wechsel der Register; Neuladen setzt
//                       zurueck.
//   quelle „datenbank“  (mit Sitzung) Jede Aenderung geht an eine Server
//                       Action (`app/(app)/pinnwand/actions.ts`). Bis sie
//                       antwortet, zeigt `useOptimistic` das Ergebnis —
//                       eine abgehakte Karte springt sofort nach
//                       „Erledigt“ —, danach gilt der Bestand, den ihre
//                       Antwort mitbringt. Lehnt der Server ab, faellt
//                       die Anzeige zurueck, der Aufrufer bekommt `{ error }`.
//
// Die Reihenfolge der Liste ist die Reihenfolge im Brett; mit Sitzung
// speichert sie `Task.sortOrder`.
// ============================================================
import { createContext, startTransition, useCallback, useContext, useMemo, useOptimistic, useRef, useState } from "react";
import type { TaskStatus } from "@/generated/prisma/enums";
import type { ActionResult } from "@/lib/action-result";
import type { AufgabeEingabe, AufgabeView, AushangEingabe, AushangView, PinnwandBestand } from "@/lib/pinnwand/views";
import { isoJetzt } from "@/lib/gruppen/datum";
import * as server from "@/app/(app)/pinnwand/actions";

/** Zeitpunkt einer Aenderung — die echte Uhr, nicht die des Seitenaufrufs (nur in Ereignissen, nie beim Zeichnen). */
const stempel = () => isoJetzt(new Date());

export type PinnwandRechte = {
  /** `board.post` — Aushaenge schreiben, Aufgaben anlegen und pflegen. */
  schreiben: boolean;
};

type Aktionen = {
  aushangSpeichern: (e: AushangEingabe, id?: string) => Promise<ActionResult<{ aushang: AushangView }>>;
  aushangLoeschen: (id: string) => Promise<ActionResult>;
  anheften: (id: string, an: boolean) => Promise<ActionResult>;
  aufgabeSpeichern: (e: AufgabeEingabe, id?: string) => Promise<ActionResult<{ aufgabe: AufgabeView }>>;
  aufgabeStatus: (id: string, status: TaskStatus) => Promise<ActionResult>;
  /** Ins Brett ablegen: in `status`, vor `vorId` (ohne: ans Ende). */
  aufgabeAblegen: (id: string, status: TaskStatus, vorId?: string) => Promise<ActionResult>;
  aufgabeLoeschen: (id: string) => Promise<ActionResult>;
};

type Wert = PinnwandBestand & { rechte: PinnwandRechte } & Aktionen;

// ── Was eine Aenderung tut (Muster und Vorschau) ────────────

type Stand = Pick<PinnwandBestand, "aushaenge" | "aufgaben">;

type Schritt =
  | { art: "aushang"; aushang: AushangView; neu: boolean }
  | { art: "aushang-weg"; id: string }
  | { art: "anheften"; id: string; an: boolean; zeit: string }
  | { art: "aufgabe"; aufgabe: AufgabeView; neu: boolean }
  | { art: "status"; id: string; status: TaskStatus; zeit: string }
  | { art: "ablegen"; id: string; status: TaskStatus; vorId?: string; zeit: string }
  | { art: "aufgabe-weg"; id: string };

function anwenden(s: Stand, x: Schritt): Stand {
  switch (x.art) {
    case "aushang":
      return {
        ...s,
        aushaenge: x.neu ? [x.aushang, ...s.aushaenge] : s.aushaenge.map((a) => (a.id === x.aushang.id ? x.aushang : a)),
      };
    case "aushang-weg":
      return { ...s, aushaenge: s.aushaenge.filter((a) => a.id !== x.id) };
    case "anheften":
      return { ...s, aushaenge: s.aushaenge.map((a) => (a.id === x.id ? { ...a, isPinned: x.an, updatedAt: x.zeit } : a)) };
    case "aufgabe": {
      if (x.neu) return { ...s, aufgaben: [...s.aufgaben, x.aufgabe] };
      const alt = s.aufgaben.find((a) => a.id === x.aufgabe.id);
      // Im Dialog auf „Erledigt“ gesetzt: oben in „Erledigt“, wie mit dem Haken.
      return {
        ...s,
        aufgaben:
          alt && alt.status !== "DONE" && x.aufgabe.status === "DONE"
            ? [x.aufgabe, ...s.aufgaben.filter((a) => a.id !== x.aufgabe.id)]
            : s.aufgaben.map((a) => (a.id === x.aufgabe.id ? x.aufgabe : a)),
      };
    }
    case "status": {
      // Erledigtes steht in „Erledigt“ oben, was zurueckgeholt wird, ans Ende seiner Spalte.
      const karte = s.aufgaben.find((a) => a.id === x.id);
      if (!karte || karte.status === x.status) return s;
      const neu = { ...karte, status: x.status, updatedAt: x.zeit };
      const rest = s.aufgaben.filter((a) => a.id !== x.id);
      return { ...s, aufgaben: x.status === "DONE" ? [neu, ...rest] : [...rest, neu] };
    }
    case "ablegen": {
      const karte = s.aufgaben.find((a) => a.id === x.id);
      if (!karte || x.vorId === x.id) return s;
      const rest = s.aufgaben.filter((a) => a.id !== x.id);
      const neu = karte.status === x.status ? karte : { ...karte, status: x.status, updatedAt: x.zeit };
      const ziel = x.vorId ? rest.findIndex((a) => a.id === x.vorId) : -1;
      if (ziel < 0) return { ...s, aufgaben: [...rest, neu] };
      return { ...s, aufgaben: [...rest.slice(0, ziel), neu, ...rest.slice(ziel)] };
    }
    case "aufgabe-weg":
      return { ...s, aufgaben: s.aufgaben.filter((a) => a.id !== x.id) };
  }
}

const FEHLGESCHLAGEN = "Das hat nicht geklappt — bitte noch einmal versuchen.";

const Kontext = createContext<Wert | null>(null);

export function PinnwandBestandProvider({
  bestand,
  rechte,
  children,
}: {
  bestand: PinnwandBestand;
  rechte: PinnwandRechte;
  children: React.ReactNode;
}) {
  const ausDb = bestand.quelle === "datenbank";
  const [lokal, setLokal] = useState<Stand>(() => ({ aushaenge: bestand.aushaenge, aufgaben: bestand.aufgaben }));
  const vomServer = useMemo<Stand>(() => ({ aushaenge: bestand.aushaenge, aufgaben: bestand.aufgaben }), [bestand.aushaenge, bestand.aufgaben]);
  const [jetzt, vorab] = useOptimistic(ausDb ? vomServer : lokal, anwenden);
  const { aushaenge, aufgaben } = jetzt;
  const zaehler = useRef(0);
  const { ich } = bestand;

  const amServer = useCallback(
    <T extends object>(schritt: Schritt | null, aufruf: () => Promise<ActionResult<T>>): Promise<ActionResult<T>> =>
      new Promise((fertig) => {
        startTransition(async () => {
          if (schritt) vorab(schritt);
          try {
            fertig(await aufruf());
          } catch (e) {
            console.error(e);
            fertig({ error: FEHLGESCHLAGEN });
          }
        });
      }),
    [vorab],
  );

  const imMuster = useCallback(<T extends object>(schritt: Schritt, ergebnis: T): Promise<ActionResult<T>> => {
    setLokal((s) => anwenden(s, schritt));
    return Promise.resolve({ success: true, ...ergebnis });
  }, []);

  const aushangSpeichern = useCallback(
    (e: AushangEingabe, id?: string) => {
      const alt = id ? aushaenge.find((a) => a.id === id) : undefined;
      if (ausDb) {
        return amServer(alt ? { art: "aushang", aushang: { ...alt, ...e }, neu: false } : null, () => server.aushangSpeichern(id ?? null, e));
      }
      const zeit = stempel();
      const neu: AushangView = alt
        ? { ...alt, ...e, updatedAt: zeit }
        : { id: `p-neu-${++zaehler.current}`, ...e, authorId: ich.id, authorName: ich.name, createdAt: zeit, updatedAt: zeit };
      return imMuster({ art: "aushang", aushang: neu, neu: !alt }, { aushang: neu });
    },
    [ausDb, amServer, imMuster, aushaenge, ich],
  );

  const aushangLoeschen = useCallback(
    (id: string) => {
      const schritt: Schritt = { art: "aushang-weg", id };
      return ausDb ? amServer(schritt, () => server.aushangLoeschen(id)) : imMuster(schritt, {});
    },
    [ausDb, amServer, imMuster],
  );

  const anheften = useCallback(
    (id: string, an: boolean) => {
      const schritt: Schritt = { art: "anheften", id, an, zeit: stempel() };
      return ausDb ? amServer(schritt, () => server.aushangAnheften(id, an)) : imMuster(schritt, {});
    },
    [ausDb, amServer, imMuster],
  );

  const aufgabeSpeichern = useCallback(
    (e: AufgabeEingabe, id?: string) => {
      const alt = id ? aufgaben.find((a) => a.id === id) : undefined;
      if (ausDb) {
        return amServer(alt ? { art: "aufgabe", aufgabe: { ...alt, ...e }, neu: false } : null, () => server.aufgabeSpeichern(id ?? null, e));
      }
      const zeit = stempel();
      const neu: AufgabeView = alt
        ? { ...alt, ...e, updatedAt: zeit }
        : { id: `t-neu-${++zaehler.current}`, ...e, eventId: null, eventTitle: null, createdByName: ich.name, createdAt: zeit, updatedAt: zeit };
      return imMuster({ art: "aufgabe", aufgabe: neu, neu: !alt }, { aufgabe: neu });
    },
    [ausDb, amServer, imMuster, aufgaben, ich],
  );

  const aufgabeStatus = useCallback(
    (id: string, status: TaskStatus) => {
      const schritt: Schritt = { art: "status", id, status, zeit: stempel() };
      return ausDb ? amServer(schritt, () => server.aufgabeStatusSetzen(id, status)) : imMuster(schritt, {});
    },
    [ausDb, amServer, imMuster],
  );

  const aufgabeAblegen = useCallback(
    (id: string, status: TaskStatus, vorId?: string) => {
      const schritt: Schritt = { art: "ablegen", id, status, vorId, zeit: stempel() };
      if (!ausDb) return imMuster(schritt, {});
      // Die Spalte, wie sie nach dem Ablegen dasteht — so speichert sie der Server.
      const reihenfolge = anwenden({ aushaenge, aufgaben }, schritt)
        .aufgaben.filter((a) => a.status === status)
        .map((a) => a.id);
      return amServer(schritt, () => server.aufgabeAblegen(id, status, reihenfolge));
    },
    [ausDb, amServer, imMuster, aushaenge, aufgaben],
  );

  const aufgabeLoeschen = useCallback(
    (id: string) => {
      const schritt: Schritt = { art: "aufgabe-weg", id };
      return ausDb ? amServer(schritt, () => server.aufgabeLoeschen(id)) : imMuster(schritt, {});
    },
    [ausDb, amServer, imMuster],
  );

  const wert = useMemo<Wert>(
    () => ({
      ...bestand,
      aushaenge,
      aufgaben,
      rechte,
      aushangSpeichern,
      aushangLoeschen,
      anheften,
      aufgabeSpeichern,
      aufgabeStatus,
      aufgabeAblegen,
      aufgabeLoeschen,
    }),
    [bestand, aushaenge, aufgaben, rechte, aushangSpeichern, aushangLoeschen, anheften, aufgabeSpeichern, aufgabeStatus, aufgabeAblegen, aufgabeLoeschen],
  );

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function usePinnwand(): Wert {
  const wert = useContext(Kontext);
  if (!wert) throw new Error("usePinnwand steht nur unter /pinnwand (PinnwandBestandProvider).");
  return wert;
}
